import { useCallback, useEffect, useRef, useState } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { AllInventoriesPage } from '../Inventory/Page/AllInventoriesPage';
import { InventoryDetailSheet } from '../Inventory/Sections/InventoryDetailSheet';
import { CreateInventoryDialog } from '../Inventory/CreateInventory/CreateInventoryDialog';
import { DataTableFooter } from '../DataTable/DataTableFooter';
import { ResponsivePageHeader } from '../layouts/ResponsivePageHeader';
import { PageHeaderSearchBar } from '../layouts/PageHeader';
import { openMobileSidebar } from '../MobileSidebar/mobileSidebarEvents';
import { Page } from '../layouts/Page';
import { SEARCH_BAR_WIDTH } from '../../constants';
import { useToast } from '@/hooks/use-toast';
import { useInventories } from '../../hooks/useInventories';
import { useInventoryRouterSearch } from '../../hooks/useInventoryRouterSearch';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { searchQueryParam } from '@/react/utils/searchQuery';
import type { SortingState } from '@tanstack/react-table';
import { useTranslation } from '../../hooks/useTranslation';
import { can, CAPABILITIES, type CurrentUser } from '../../lib/capabilities';
import type { InventoryTranslations } from '../Inventory/types';
import type { InventoriesResponse } from '../../api/inventories';

interface InventoryManagementProps {
  translations?: InventoryTranslations;
  initialData?: InventoriesResponse;
  currentUser?: CurrentUser;
}

const DEFAULT_TRANSLATIONS: InventoryManagementProps['translations'] = {};
const DEBOUNCE_DELAY = 300;

function HeaderInventoryLabel({ count, label }: { count: number; label?: string }) {
  return (
    <span className="font-inter text-sm font-normal leading-[1.5] text-grey">
      {(label || 'Total inventory items (%{count})').replace('%{count}', count.toLocaleString())}
    </span>
  );
}

export default function InventoryManagement({
  translations = DEFAULT_TRANSLATIONS,
  initialData,
  currentUser,
}: InventoryManagementProps) {
  const { t } = useTranslation();
  // RBAC: the backend policy is the source of truth; this only hides write
  // affordances (create / edit / status) from read-only users.
  const canWrite = can(currentUser, CAPABILITIES.INVENTORIES_WRITE);
  const router = useInventoryRouterSearch();
  const { search } = router;

  // Local search input state — debounce before syncing to URL/API
  const [searchQuery, setSearchQuery] = useState(search.q);
  const debouncedSearch = useDebouncedValue(searchQuery, DEBOUNCE_DELAY);

  // Sync debounced search to URL
  useEffect(() => {
    if (debouncedSearch !== search.q) {
      router.setQuery(debouncedSearch);
    }
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Convert URL sort params to TanStack SortingState
  const sorting: SortingState = search.sort
    ? [{ id: search.sort, desc: search.direction === 'desc' }]
    : [];

  const handleSortingChange = useCallback((newSorting: SortingState) => {
    if (newSorting.length > 0) {
      router.setSort(newSorting[0].id, newSorting[0].desc ? 'desc' : 'asc');
    } else {
      // Sorting cleared — let backend use its default sort
      router.navigate({ sort: '', direction: '', page: 1 });
    }
  }, [router]);

  useEffect(() => {
    const main = document.querySelector('main');
    if (main) {
      main.classList.add('overflow-hidden');
      return () => { main.classList.remove('overflow-hidden'); };
    }
  }, []);

  const [createModalOpen, setCreateModalOpen] = useState(false);

  const apiParams = {
    status: search.status,
    q: searchQueryParam(search.q),
    page: search.page,
    perPage: search.perPage,
    ...(search.sort ? { sortBy: search.sort, sortDirection: search.direction as 'asc' | 'desc' } : {}),
  };

  // Only use server-rendered initialData on the very first render to avoid a loading flash.
  // After that, React Query owns the cache and refetches as needed.
  const initialDataRef = useRef(initialData);
  useEffect(() => { initialDataRef.current = undefined; }, []);

  const isDefaultQuery = search.status === 'active' && !search.q && search.page === 1 && !search.sort;

  const { data, isLoading, isFetching } = useInventories({
    params: apiParams,
    initialData: isDefaultQuery ? initialDataRef.current : undefined,
  });

  const inventories = data?.inventories ?? [];
  const totalCount = data?.pagination?.total ?? 0;
  const totalUnfiltered = data?.total_unfiltered ?? 0;

  const [selectedInventoryId, setSelectedInventoryId] = useState<number | null>(null);

  const selectedInventory =
    inventories.find((inv) => inv.id === selectedInventoryId) ?? null;

  // Close the sheet if the selected inventory disappears from the list (e.g., after status change)
  useEffect(() => {
    if (selectedInventoryId !== null && !selectedInventory) {
      setSelectedInventoryId(null);
    }
  }, [selectedInventoryId, selectedInventory]);

  const { dismiss: dismissToasts } = useToast();

  const handleInventorySelect = useCallback(
    (inventory: { id: number }) => {
      dismissToasts();
      setSelectedInventoryId(inventory.id);
    },
    []
  );

  const totalPages = Math.ceil(totalCount / search.perPage);

  return (
    <>
      <Toaster />
      <Page>
        <Page.Header className="py-6 md:py-0">
          <ResponsivePageHeader
            mobile={{
              title: t('index.title'),
              count: isLoading ? '…' : totalCount.toLocaleString(),
              searchValue: searchQuery,
              onSearchChange: setSearchQuery,
              searchPlaceholder: t('search.placeholder'),
              searchAriaLabel: t('search.aria_label'),
              openNavigationAriaLabel: t('header.open_navigation'),
              openSearchAriaLabel: t('header.open_search'),
              closeSearchAriaLabel: t('header.close_search'),
              onOpenNavigation: openMobileSidebar,
            }}
          >
            <div className="shrink-0">
              <HeaderInventoryLabel count={totalUnfiltered} label={t('header.total_unfiltered')} />
            </div>
            <div
              className="flex-1 min-w-0 flex justify-center"
              style={{ flexBasis: SEARCH_BAR_WIDTH }}
            >
              <PageHeaderSearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={t('search.placeholder')}
                ariaLabel={t('search.aria_label')}
              />
            </div>
            <div
              className="min-w-0 invisible"
              aria-hidden="true"
              style={{ flexShrink: 99 }}
            >
              <HeaderInventoryLabel count={totalUnfiltered} label={t('header.total_unfiltered')} />
            </div>
          </ResponsivePageHeader>
        </Page.Header>
        <Page.Body>
          <AllInventoriesPage
            currentStatus={search.status}
            totalCount={totalCount}
            onStatusChange={router.setStatus}
            translations={translations}
            isLoading={isLoading}
            isFetching={isFetching}
            inventories={inventories}
            currentPage={search.page}
            pageSize={search.perPage}
            onPageChange={router.setPage}
            onPerPageChange={router.setPerPage}
            onRowClick={handleInventorySelect}
            onCreateNew={() => setCreateModalOpen(true)}
            canWrite={canWrite}
            sorting={sorting}
            onSortingChange={handleSortingChange}
          />
        </Page.Body>
        <Page.Footer>
          <DataTableFooter
            currentPage={search.page}
            totalPages={totalPages}
            totalCount={totalCount}
            perPage={search.perPage}
            onPageChange={router.setPage}
            onPerPageChange={router.setPerPage}
            className="flex flex-col justify-center h-full px-6 sm:px-8 lg:px-16"
          />
        </Page.Footer>
      </Page>

      <CreateInventoryDialog
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
      />

      <InventoryDetailSheet
        inventory={selectedInventory}
        open={selectedInventoryId !== null}
        onOpenChange={(open) => { if (!open) setSelectedInventoryId(null); }}
        canWrite={canWrite}
      />
    </>
  );
}
